// Four sign palettes in the site's own colours (cream paper, warm brown ink,
// the orange dot). The QR is drawn straight on the paper in the palette's
// colours, never on a white box. Art direction: PR #153.

export const QR_SIGN_DESIGN_IDS = ["creme", "salvie", "rosa", "mokka"] as const;
export type QrSignDesignId = (typeof QR_SIGN_DESIGN_IDS)[number];

export const QR_SIGN_DECORATIONS = {
  /** Eucalyptus sprigs off the bottom-left and top-right corners. */
  eucalyptus: "eucalyptus",
  /** A hairline frame and one sprig off the bottom-right corner. */
  frameAndSprig: "frame-and-sprig",
  /** Two hairline frames, no image. */
  doubleFrame: "double-frame",
  /** Only the dot under the heading. */
  dot: "dot",
} as const;
export type QrSignDecoration =
  (typeof QR_SIGN_DECORATIONS)[keyof typeof QR_SIGN_DECORATIONS];

export interface QrSignQrStyle {
  /** Module colour. */
  modules: string;
  /** Quiet-zone colour: the paper, or the panel when there is one. */
  background: string;
  /** A panel behind the code, for palettes whose paper can't carry it. */
  panel?: { frame: string };
}

export interface QrSignDesign {
  id: QrSignDesignId;
  background: string;
  /** Heading and scan label. */
  text: string;
  /** Body and tasks. */
  secondary: string;
  /** Frames and rules. */
  accent: string;
  /** The printed URL. */
  url: string;
  dot: string;
  qr: QrSignQrStyle;
  decoration: QrSignDecoration;
}

const ORANGE_DOT = "#f9b144";

export const QR_SIGN_DESIGNS: readonly QrSignDesign[] = [
  {
    id: "creme",
    background: "#f7f1ea",
    text: "#2b231d",
    secondary: "#7a6b5f",
    accent: "#9c5520",
    url: "#9c5520",
    dot: ORANGE_DOT,
    qr: { modules: "#2b231d", background: "#f7f1ea" },
    decoration: QR_SIGN_DECORATIONS.eucalyptus,
  },
  {
    id: "salvie",
    background: "#edf0e6",
    text: "#3a5236",
    secondary: "#4a6f4e",
    accent: "#567951",
    // The sage accent is 4.3:1 on the paper, so the URL uses the body green.
    url: "#4a6f4e",
    dot: ORANGE_DOT,
    qr: { modules: "#3a5236", background: "#edf0e6" },
    decoration: QR_SIGN_DECORATIONS.frameAndSprig,
  },
  {
    id: "rosa",
    background: "#f4e6e1",
    text: "#4a2f2a",
    secondary: "#7d5a48",
    accent: "#a64e39",
    url: "#a64e39",
    dot: ORANGE_DOT,
    qr: { modules: "#4a2f2a", background: "#f4e6e1" },
    decoration: QR_SIGN_DECORATIONS.doubleFrame,
  },
  {
    id: "mokka",
    background: "#2b231d",
    text: "#f7f1ea",
    secondary: "#d9cbbd",
    accent: ORANGE_DOT,
    url: ORANGE_DOT,
    dot: ORANGE_DOT,
    // Light modules on the dark paper (inverted) fail decoders that don't try
    // inversion, so the code sits on a cream panel with the page's brown
    // modules and an orange hairline: the same two colours, flipped.
    qr: { modules: "#2b231d", background: "#f7f1ea", panel: { frame: ORANGE_DOT } },
    decoration: QR_SIGN_DECORATIONS.dot,
  },
];

export const DEFAULT_QR_SIGN_DESIGN = QR_SIGN_DESIGNS[0];

/**
 * Ids of the first six designs, which came from the QR card templates. They
 * were never stored or put in links, but map them to the nearest palette so
 * an old id still finds a design.
 */
export const LEGACY_QR_SIGN_DESIGN_IDS: Readonly<Record<string, QrSignDesignId>> = {
  "camera-1": "creme",
  "camera-2": "creme",
  "camera-3": "salvie",
  "default-qr-card-2": "rosa",
  "eucalyptus-1": "salvie",
  "gatsby-1": "mokka",
};

/** The design for an id, an old id's nearest palette, or the default. */
export function resolveQrSignDesign(id: string | null | undefined): QrSignDesign {
  const key = id ? (LEGACY_QR_SIGN_DESIGN_IDS[id] ?? id) : "";
  return QR_SIGN_DESIGNS.find((design) => design.id === key) ?? DEFAULT_QR_SIGN_DESIGN;
}

export function normalizeHexColor(value: unknown, fallback: string): string {
  if (typeof value !== "string") {
    return fallback;
  }
  const trimmed = value.trim().toLowerCase();
  if (/^#[0-9a-f]{6}$/.test(trimmed)) {
    return trimmed;
  }
  if (/^#[0-9a-f]{3}$/.test(trimmed)) {
    return `#${trimmed
      .slice(1)
      .split("")
      .map((digit) => digit + digit)
      .join("")}`;
  }
  return fallback;
}

function relativeLuminance(hex: string): number {
  const value = Number.parseInt(hex.slice(1), 16);
  const channel = (shift: number): number => {
    const c = ((value >> shift) & 0xff) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(16) + 0.7152 * channel(8) + 0.0722 * channel(0);
}

export function contrastRatio(a: string, b: string): number {
  const [light, dark] = [relativeLuminance(a), relativeLuminance(b)].sort(
    (x, y) => y - x
  );
  return (light + 0.05) / (dark + 0.05);
}
